import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { WarningAltOutlined } from '@duxton-web/icons';
import {
  Alert,
  Card,
  ConfigProvider,
  Form,
  Input,
  Select,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import { produce } from 'immer';
import _, { union } from 'lodash';

import ProductEntitySelect from '@/common/components/FilterDrawer/ProductEntitySelect';
import BackendSelect from '@/common/selects/BackendSelect';
import CommonSelect from '@/common/selects/CommonSelect';
import TechFamilySelect from '@/common/selects/TechFamilySelect';
import { FEATURE_FLAGS, GRABX_SLACK_CHANNEL, VariableState } from '@/constants';
import { FACETS } from '@/constants/facets';
import { useUserRolesContext } from '@/contexts/UserRolesContext';
import {
  SyncedFromGrabOverlay,
  useIsSyncedFromGrab,
} from '@/features/tenant-sync';
import {
  useVariableContext,
  useVariableDispatchContext,
} from '@/features/variable/contexts/variable-context';
import ManageProductEntities from '@/features/variable/settings/product-entity/manage-entities';
import {
  updateVariableIsValid,
  variableDetailUpdate,
} from '@/features/variable/store/actions/variable/action-creators';
import { isFieldChanged } from '@/features/variable/utils';
import { useFetchDomainsMetadata } from '@/hooks';
import { useGetRollout } from '@/hooks/usePlannerQueries/variable';
import Service, { VariableInfo } from '@/lib/App';
import { UserAction } from '@/utils/accessControl';
import { getDomainAssignBy } from '@/utils/domain';
import { getMandatoryFacetsByDomain } from '@/utils/facet';
import { deepMerge } from '@/utils/object';
import { isAdmin } from '@/utils/user';

// blacklistedTFPrefixes is a variable to soft deprecate TFs during variable creation / save
const blacklistedTFPrefixes = ['FOOD', 'GRABMART', 'GRABEXPRESS'];

const MinimumMandatoryFacets = [FACETS.Country];

const MANAGED_BY_PLATFORM_OPTIONS = [
  { value: 'Adaptive Experiments', label: 'Adaptive Experiments' },
  { value: 'Cradle', label: 'Cradle' },
  { value: 'Jaya', label: 'Jaya' },
  { value: 'Kios CMS', label: 'Kios CMS' },
  { value: 'Loyalty CMS', label: 'Loyalty CMS' },
  { value: 'Marketwatch', label: 'Marketwatch' },
  { value: 'Pricing Control Centre', label: 'Pricing Control Centre' },
  { value: 'Sandbox-DAX', label: 'Sandbox-DAX' },
  { value: 'Stellar', label: 'Stellar' },
  { value: 'Supply Shaping Portal', label: 'Supply Shaping Portal' },
  { value: 'Zeus', label: 'Zeus' },
];

const MainDetails = () => {
  const {
    currentVariable,
    variable,
    disabled: variableDisabled,
    variableDiff,
  } = useVariableContext();

  // Check if variable has active rollout
  const { data: rolloutData } = useGetRollout(variable?.name || '', {
    enabled: !!variable?.name,
  });

  const isArchived = variable?.state === VariableState.ARCHIVED;
  const hasActiveRollout = !!rolloutData;
  const disabled = isArchived || variableDisabled;
  const isServiceAuthZEnabled = Service.cache.isFeatureFlagOn(
    FEATURE_FLAGS.SERVICE_AUTHZ,
  );
  const typeSelectDisabled = disabled || hasActiveRollout;

  return (
    <Card>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Typography.Text strong>Main Details</Typography.Text>

        <Form.Item
          label="Name"
          name="name"
          validateFirst
          rules={[
            {
              required: true,
              message: 'Enter variable name',
            },
            {
              pattern: new RegExp(/^[a-zA-Z]/),
              message: 'First character should be a letter',
            },
            {
              pattern: new RegExp(/^[a-zA-Z][0-9a-zA-Z_-]*$/),
              message: 'Only alphanumeric, dash and underscore are allowed',
            },
          ]}
        >
          <Input
            id="variable-settings-detail-name"
            className={
              isFieldChanged(variableDiff, 'name')
                ? 'changelog-item-variable-modified'
                : ''
            }
            data-testid="variable-settings-detail-name"
            disabled={!!currentVariable?.name}
            placeholder="Add Variable Name"
          />
        </Form.Item>

        <Form.Item
          label={
            hasActiveRollout ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                Variable Type
                <Tooltip title="Variable type cannot be changed during active rollout">
                  <WarningAltOutlined style={{ color: '#faad14' }} />
                </Tooltip>
              </div>
            ) : (
              'Variable Type'
            )
          }
          name="type"
          required
          rules={[
            {
              required: true,
              message: 'Select variable type',
            },
          ]}
        >
          <CommonSelect
            id="variable-settings-detail-type"
            className={
              isFieldChanged(variableDiff, 'type')
                ? 'changelog-item-variable-modified'
                : ''
            }
            data-testid="variable-settings-detail-variable-type"
            style={{
              minWidth: '160px',
            }}
            disabled={typeSelectDisabled}
            placeholder="Select Data Type"
            values={Service.cache!.getAllVariableTypes().map((val) => ({
              id: val,
            }))}
          />
        </Form.Item>

        <Form.Item
          label="Description"
          name="desc"
          required
          rules={[
            {
              required: true,
              message: 'Enter variable description',
            },
          ]}
        >
          <Input.TextArea
            id="variable-settings-detail-desc"
            className={
              isFieldChanged(variableDiff, 'desc')
                ? 'changelog-item-variable-modified'
                : ''
            }
            data-testid="variable-settings-detail-desc"
            disabled={disabled}
            autoSize={{
              minRows: 4,
              maxRows: 4,
            }}
            maxLength={120}
            showCount={!disabled}
            placeholder="Add more information on what this variable changes and how it will affect users. (i.e., when the bookingConfirmation variable is enabled, it will show the user a confirmation message for their completed booking.)"
          />
        </Form.Item>

        <Form.Item
          label="Documentation Link"
          name={['metadata', 'documentUrl']}
          rules={[
            {
              required: true,
              message: 'Documentation link is required',
            },
            {
              validator: (_, value) => {
                if (value) {
                  const splittedValues = value.split(',');
                  try {
                    const invalidValues = splittedValues.filter(
                      (val) => !new URL(val),
                    );
                    if (invalidValues.length) {
                      return Promise.reject(
                        new Error('Invalid documentation link'),
                      );
                    }
                  } catch {
                    return Promise.reject(
                      new Error('Invalid documentation link'),
                    );
                  }
                }
                return Promise.resolve();
              },
            },
          ]}
          getValueProps={(value) => ({
            value: value?.split(',') || undefined,
          })}
          normalize={(value) => value?.join(',') || undefined}
        >
          <Select
            id="variable-settings-detail-metadata-documentUrl"
            className={
              isFieldChanged(variableDiff, 'metadata/documentUrl')
                ? 'changelog-item-variable-modified'
                : ''
            }
            maxTagTextLength={50}
            data-testid="variable-settings-detail-url"
            mode="tags"
            tagRender={(props) => {
              const { label, value } = props;
              return (
                <Tag
                  {...props}
                  onClick={() => {
                    window.open(value, '_blank');
                  }}
                  style={{ cursor: 'pointer', maxWidth: '100%' }}
                >
                  {label}
                </Tag>
              );
            }}
            disabled={disabled}
            placeholder="Product Spec / Eng Spec / Jira Ticket / Gitlab link that describes what your variable does"
            style={{ width: '100%', cursor: 'text' }}
            tokenSeparators={[',']}
            options={[]}
            dropdownRender={null}
            notFoundContent={null}
            open={false}
            showSearch={false}
          />
        </Form.Item>

        <Form.Item
          label="Managed By Platform"
          name={['metadata', 'managedByPlatform']}
          normalize={(value) => value || undefined}
        >
          <Select
            id="variable-settings-detail-metadata-managedByPlatform"
            data-testid="variable-settings-detail-managed-by-platform"
            disabled={disabled}
            placeholder="Select platform"
            allowClear
            options={MANAGED_BY_PLATFORM_OPTIONS}
          />
        </Form.Item>

        {variable?.metadata?.managedByPlatform && (
          <Alert
            type="warning"
            showIcon
            data-testid="managed-by-platform-warning"
            message="Once saved, users will not be able to update rollout through UIs."
          />
        )}
        {isServiceAuthZEnabled && (
          <Form.Item
            layout="horizontal"
            labelAlign="left"
            name="criticality"
            valuePropName="checked"
            colon={false}
            label={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography.Text>
                  Critical Variable{' '}
                  <Typography.Metadata
                    style={{
                      fontSize: '14px',
                      color: '#A3A3A3',
                    }}
                  >
                    (Users must apply for corresponding role to access)
                  </Typography.Metadata>
                </Typography.Text>
              </div>
            }
            labelCol={{
              span: 22,
            }}
            getValueFromEvent={(checked: boolean) => (checked ? 1 : 0)}
            getValueProps={(value) => ({ checked: value === 1 })}
          >
            <Switch
              id="variable-settings-detail-criticality"
              className={
                isFieldChanged(variableDiff, 'criticality')
                  ? 'changelog-item-variable-modified'
                  : ''
              }
              data-testid="variable-settings-detail-criticality"
              disabled={disabled}
            />
          </Form.Item>
        )}
      </div>
    </Card>
  );
};

const ProductEntity = ({
  onManageEntitiesClick = () => {},
  onManageEntitiesCancel = () => {},
}: {
  onManageEntitiesClick?: () => void;
  onManageEntitiesCancel?: () => void;
}) => {
  const {
    variable,
    disabled: variableDisabled,
    variableDiff,
  } = useVariableContext();

  const isArchived = variable?.state === VariableState.ARCHIVED;
  const disabled = isArchived || variableDisabled;

  return (
    <div
      style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
      }}
    >
      <Form.Item
        style={{
          flex: '1 0 auto',
        }}
        label={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            Product Name
            <a
              href="https://techdocs.grab.com/product-docs/grabx/grabx-user-guide/variables/product-entity/"
              target="_blank"
              rel="noreferrer"
            >
              (Guide to Product Names)
            </a>
          </div>
        }
        name="productEntity"
        required
        rules={[
          {
            required: true,
            message: 'Select product name',
          },
        ]}
      >
        <ProductEntitySelect
          id="variable-settings-detail-productEntity"
          className={
            isFieldChanged(variableDiff, 'productEntity')
              ? 'changelog-item-variable-modified'
              : ''
          }
          data-testid="variable-settings-detail-product-entity"
          disabled={disabled}
          placeholder="Select product name"
        />
      </Form.Item>
      {!disabled && (
        <Form.Item
          style={{
            flex: '0 1 auto',
          }}
          label=" "
        >
          <ManageProductEntities
            onManageEntitiesClick={onManageEntitiesClick}
            onManageEntitiesCancel={onManageEntitiesCancel}
          />
        </Form.Item>
      )}
    </div>
  );
};

const Owners = ({
  onManageEntitiesClick = () => {},
  onManageEntitiesCancel = () => {},
}: {
  onManageEntitiesClick?: () => void;
  onManageEntitiesCancel?: () => void;
}) => {
  const { permissionUtils } = useUserRolesContext();
  const {
    variable,
    disabled: variableDisabled,
    variableDiff,
  } = useVariableContext();

  const isArchived = variable?.state === VariableState.ARCHIVED;
  const disabled = isArchived || variableDisabled;
  const actionRequiredToSelectTF = UserAction.CREATE_VARIABLE_USING_CURRENT_TF;

  const isTFPermitted = (prefix: string) =>
    permissionUtils.canPerformAction(actionRequiredToSelectTF, [prefix]);

  const techFamilies = _.sortBy(
    Service.cache!.getAllTechFamilies().filter(
      (tf) => !blacklistedTFPrefixes.includes(tf.prefix),
    ),
    ({ prefix }) => !isTFPermitted(prefix),
  );

  const getAccessControlSelectProps = () => {
    return {
      disabledMap: Object.fromEntries(
        techFamilies.map((tf) => [tf.prefix, !isTFPermitted(tf.prefix)]),
      ),
      metaMap: Object.fromEntries(
        techFamilies
          .filter(({ prefix }) => !isTFPermitted(prefix))
          .map(({ prefix }) => [
            prefix,
            permissionUtils.generateExplanation({
              action: actionRequiredToSelectTF,
              techFamilies: [prefix],
            }),
          ]),
      ),
      useMeta: true,
    };
  };

  const enableServiceAuthZ =
    Service.cache!.isFeatureFlagOn(FEATURE_FLAGS.SERVICE_AUTHZ) &&
    Service.cache!.isFeatureFlagOn(FEATURE_FLAGS.INCLUDE_HELIX_TECH_FAMILIES);

  return (
    <Card>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Typography.Text strong>Owners</Typography.Text>

        <Form.Item
          label="Tech Family"
          name="techFamily"
          required
          rules={[
            {
              required: true,
              message: 'Select Tech Family',
            },
          ]}
        >
          {enableServiceAuthZ ? (
            <TechFamilySelect
              id="variable-settings-detail-techFamily"
              className={
                isFieldChanged(variableDiff, 'techFamily')
                  ? 'changelog-item-variable-modified'
                  : ''
              }
              data-testid="variable-settings-detail-tech-family"
              disabled={disabled}
            />
          ) : (
            <CommonSelect
              id="variable-settings-detail-techFamily"
              className={
                isFieldChanged(variableDiff, 'techFamily')
                  ? 'changelog-item-variable-modified'
                  : ''
              }
              data-testid="variable-settings-detail-tech-family"
              disabled={disabled}
              idKey="prefix"
              valueFn={(item) => (
                <span>{`${item?.title} (${item?.prefix})`}</span>
              )}
              values={techFamilies}
              placeholder="Select tech family that owns this variable"
              {...getAccessControlSelectProps()}
            />
          )}
        </Form.Item>

        <ProductEntity
          onManageEntitiesClick={onManageEntitiesClick}
          onManageEntitiesCancel={onManageEntitiesCancel}
        />

        <Form.Item
          label="Engineering Owner"
          name={['metadata', 'technicalOwner']}
          required
          rules={[
            {
              required: true,
              message: 'Select Engineering Owner',
            },
          ]}
        >
          <CommonSelect
            id="variable-settings-detail-metadata-technicalOwner"
            className={
              isFieldChanged(variableDiff, 'metadata/technicalOwner')
                ? 'changelog-item-variable-modified'
                : ''
            }
            data-testid="variable-settings-detail-engineering-manager"
            disabled={disabled}
            idKey="email"
            values={Service.cache!.getBasicUsers().filter(
              ({ email }) => !!email,
            )}
            placeholder="Select engineering owner"
          />
        </Form.Item>

        <Form.Item
          label="Product Owner"
          name={['metadata', 'productOwner']}
          required
          rules={[
            {
              required: true,
              message: 'Select Product Owner',
            },
          ]}
        >
          <CommonSelect
            id="variable-settings-detail-metadata-productOwner"
            className={
              isFieldChanged(variableDiff, 'metadata/productOwner')
                ? 'changelog-item-variable-modified'
                : ''
            }
            data-testid="variable-settings-detail-product-manager"
            disabled={disabled}
            idKey="email"
            values={Service.cache!.getBasicUsers().filter(
              ({ email }) => !!email,
            )}
            placeholder="Select product owner"
          />
        </Form.Item>
      </div>
    </Card>
  );
};

const AnalyticsExperimentation = () => {
  const { isLoading, data } = useFetchDomainsMetadata();
  const {
    variable,
    disabled: variableDisabled,
    variableDiff,
  } = useVariableContext();

  const isArchived = variable?.state === VariableState.ARCHIVED;
  const disabled = isArchived || variableDisabled;

  return (
    <Card>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Typography.Text strong>Experimentation</Typography.Text>

        <Form.Item
          name="domain"
          required
          label="Domain"
          rules={[
            {
              required: true,
              message: 'Select Domain',
            },
          ]}
        >
          <CommonSelect
            id="variable-settings-detail-domain"
            className={
              isFieldChanged(variableDiff, 'domain')
                ? 'changelog-item-variable-modified'
                : ''
            }
            style={{
              flex: '1 1',
            }}
            data-testid="variable-settings-detail-domain"
            disabled={disabled}
            idKey="name"
            labelKey="title"
            descriptionKey="description"
            values={!isLoading ? data : []}
            placeholder="Select the domain this variable will be experiment in"
          />
        </Form.Item>
      </div>
    </Card>
  );
};

const TechnicalDetails = () => {
  const {
    currentVariable,
    variable,
    disabled: variableDisabled,
    variableDiff,
  } = useVariableContext();
  const dispatch = useVariableDispatchContext();

  const form = Form.useFormInstance();
  const isArchived = variable?.state === VariableState.ARCHIVED;
  const disabled = isArchived || variableDisabled;

  const primaryServiceDescription =
    'Mention the name of the service that owns this variable';
  const subscribedServicesDescription =
    'Add your service name if you are reading this variable';

  const removedSubscribedServices = _.difference(
    currentVariable?.metadata?.subscribedServices || [],
    variable?.metadata?.subscribedServices || [],
  );

  const admin = isAdmin();
  const subscribedDisabledMap = useMemo(
    () =>
      admin
        ? {}
        : variable?.metadata?.subscribedServices?.reduce(
            (acc, serviceKey) => {
              acc[serviceKey] = true;
              return acc;
            },
            {} as Record<string, boolean>,
          ) ?? {},
    [admin, variable?.metadata?.subscribedServices],
  );

  const [mandatoryEnabledAddedFacets, setMandatoryEnabledAddedFacet] = useState<
    string[]
  >([]);

  const currentMandatoryFacets = variable?.metadata?.mandatoryFacets || [];
  const isMandatoryFacetEnabled = currentMandatoryFacets.length > 0;

  // Get selected facets from the main facets field
  const selectedFacets = variable?.facets || [];
  const allFacets = Service.cache!.getAllFacets();

  // Filter to only show facets that are already selected in the main facets field
  const availableFacets = allFacets.filter((facet) =>
    selectedFacets.includes(facet.name),
  );

  const mandatoryFacetsDisabledMap = MinimumMandatoryFacets.reduce(
    (acc, facetKey) => {
      acc[facetKey] = true;
      return acc;
    },
    {} as Record<string, boolean>,
  );

  const customTagRender = (props) => {
    const { label, value } = props;
    const isMinimumMandatory = MinimumMandatoryFacets.includes(value);

    const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
    };

    if (isMinimumMandatory) {
      return (
        <Tooltip title="Required mandatory facet - cannot be removed">
          <Tag
            {...props}
            className="common-select-custom-tag dui-select-selection-item"
            style={{ maxWidth: '100%', paddingRight: 8 }}
            onMouseDown={onMouseDown}
            closable={false}
            onClose={() => {}}
          >
            {label}
          </Tag>
        </Tooltip>
      );
    }

    return (
      <Tag
        {...props}
        className="common-select-custom-tag dui-select-selection-item"
        style={{ maxWidth: '100%' }}
        onMouseDown={onMouseDown}
      >
        {label}
      </Tag>
    );
  };

  return (
    <Card>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Typography.Text strong>Technical Details</Typography.Text>

        <Card
          styles={{
            body: {
              padding: '8px 16px',
            },
          }}
        >
          <Form.Item
            layout="horizontal"
            labelAlign="left"
            name="public"
            valuePropName="checked"
            colon={false}
            label={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography.Text>
                  Available in mobile/web.{' '}
                  <Typography.Metadata
                    style={{
                      fontSize: '14px',
                      color: '#A3A3A3',
                    }}
                  >
                    (Public Variable)
                  </Typography.Metadata>
                </Typography.Text>
              </div>
            }
            labelCol={{
              span: 22,
            }}
          >
            <Switch
              data-testid="variable-settings-detail-public-variable"
              disabled={disabled}
            />
          </Form.Item>

          <Form.Item
            layout="horizontal"
            valuePropName="checked"
            colon={false}
            label={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography.Text>
                  Updates without app restart.{' '}
                  <Typography.Metadata
                    style={{
                      fontSize: '14px',
                      color: '#A3A3A3',
                    }}
                  >
                    (Hot Variable)
                  </Typography.Metadata>
                </Typography.Text>
              </div>
            }
            labelCol={{
              span: 22,
            }}
            labelAlign="left"
            name="hot"
          >
            <Switch
              data-testid="variable-settings-detail-hot-variable"
              disabled={disabled || !form.getFieldValue('public')}
            />
          </Form.Item>
        </Card>

        <Form.Item
          required
          name="backend"
          label={
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Tooltip title={primaryServiceDescription}>
                Primary Service
              </Tooltip>
              {currentVariable?.name && !isAdmin() && (
                <Tooltip
                  title={
                    <>
                      Variable service is no longer available for editing.
                      Please raise a service request in{' '}
                      <Typography.Link
                        href={GRABX_SLACK_CHANNEL}
                        style={{ color: '#4096ff' }}
                        target="_blank"
                      >
                        #grab-x
                      </Typography.Link>
                      .
                    </>
                  }
                >
                  <WarningAltOutlined style={{ color: '#faad14' }} />
                </Tooltip>
              )}
            </div>
          }
          rules={[
            {
              required: true,
              message: 'Select Service',
            },
          ]}
        >
          <BackendSelect
            id="variable-settings-detail-backend"
            className={
              isFieldChanged(variableDiff, 'backend')
                ? 'changelog-item-variable-modified'
                : ''
            }
            data-testid="variable-settings-detail-service"
            disabled={disabled || (!!currentVariable?.name && !isAdmin())}
            placeholder={primaryServiceDescription}
          />
        </Form.Item>

        <Form.Item
          name={['metadata', 'subscribedServices']}
          label={
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Tooltip title={subscribedServicesDescription}>
                Subscribed Services
              </Tooltip>
              <Typography.Metadata
                style={{
                  color: '#A3A3A3',
                }}
              >
                (Requires X-SDK v1.1.85 or later)
              </Typography.Metadata>
            </div>
          }
        >
          <BackendSelect
            id="variable-settings-detail-metadata-subscribedServices"
            className={
              isFieldChanged(variableDiff, 'metadata/subscribedServices')
                ? 'changelog-item-variable-modified'
                : ''
            }
            data-testid="variable-settings-detail-subscribed-services"
            disabled={disabled}
            disabledMap={subscribedDisabledMap}
            multiple={true}
            placeholder={subscribedServicesDescription}
            tagRender={(props) => {
              const { value, label } = props;
              const isPreviouslySaved =
                variable?.metadata?.subscribedServices?.includes(value);

              const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
                e.preventDefault();
                e.stopPropagation();
              };

              if (!isAdmin() && !!currentVariable?.name && isPreviouslySaved) {
                return (
                  <Tooltip title="Previously saved service - cannot be removed">
                    <Tag
                      {...props}
                      className="common-select-custom-tag dui-select-selection-item previously-saved-service"
                      style={{ maxWidth: '100%' }}
                      data-testid="previously-saved-service-tag"
                      onMouseDown={onMouseDown}
                    >
                      {label}
                    </Tag>
                  </Tooltip>
                );
              }

              return (
                <Tag
                  {...props}
                  className="common-select-custom-tag dui-select-selection-item"
                  style={{ maxWidth: '100%' }}
                  onMouseDown={onMouseDown}
                >
                  {label}
                </Tag>
              );
            }}
          />
        </Form.Item>

        {/* Warning badge about removing subscribed services, listing them if any */}
        {removedSubscribedServices.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <Alert
              type="warning"
              showIcon
              data-testid="subscribed-services-warning"
              description={
                <div>
                  <div>
                    The following subscribed service(s) will lose access to this
                    variable if removed:
                  </div>
                  <ul style={{ margin: '8px 0 0 16px' }}>
                    {removedSubscribedServices.map((serviceName) => (
                      <li
                        key={serviceName}
                        data-testid={`removed-service-${serviceName}`}
                        aria-label={`Removed service: ${serviceName}`}
                      >
                        {serviceName}
                      </li>
                    ))}
                  </ul>
                  <div style={{ marginTop: 8 }}>
                    Please double-check before proceeding.
                  </div>
                </div>
              }
            />
          </div>
        )}

        <Form.Item
          label="Facets"
          name="facets"
          rules={[
            {
              required: true,
              message: 'Select Facets',
            },
          ]}
        >
          <CommonSelect
            id="variable-settings-detail-facets"
            className={
              isFieldChanged(variableDiff, 'facets')
                ? 'changelog-item-variable-modified'
                : ''
            }
            data-testid="variable-settings-detail-facets"
            disabled={disabled}
            values={Service.cache!.getAllFacets()}
            idKey="name"
            valueKey="title"
            disabledMap={variable?.metadata?.mandatoryFacets?.reduce(
              (acc, facetKey) => {
                acc[facetKey] = true;
                return acc;
              },
              {} as Record<string, boolean>,
            )}
            multiple={true}
            placeholder="Select facets to be included in the variable"
            tagRender={(tag) => {
              if (!variable?.metadata?.mandatoryFacets?.includes(tag.value))
                return (
                  <Tag
                    {...tag}
                    className="common-select-custom-tag dui-select-selection-item"
                  >
                    {tag.label}
                  </Tag>
                );

              return (
                <Tooltip title="Required mandatory facet - cannot be removed">
                  <Tag
                    className="common-select-custom-tag dui-select-selection-item"
                    closable={
                      !variable?.metadata?.mandatoryFacets?.includes(tag.value)
                    }
                  >
                    {tag.label}
                  </Tag>
                </Tooltip>
              );
            }}
          />
        </Form.Item>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography.Text>Mandatory Facets</Typography.Text>
          <Switch
            data-testid="variable-details-mandatory-facet-toggle"
            disabled={disabled}
            value={!!variable?.metadata?.mandatoryFacets?.length}
            onChange={(checked: boolean) => {
              const updatedVariable = produce(variable, (draft) => {
                if (checked) {
                  draft.metadata.mandatoryFacets = [...MinimumMandatoryFacets];
                  setMandatoryEnabledAddedFacet(
                    MinimumMandatoryFacets.filter(
                      (facet) => !draft.facets?.includes(facet),
                    ),
                  );
                  draft.facets = union(draft.facets || [], [
                    ...MinimumMandatoryFacets,
                  ]);
                } else {
                  draft.facets = _.pull(
                    draft.facets || [],
                    ...mandatoryEnabledAddedFacets,
                  );
                  draft.metadata.mandatoryFacets = null;
                }
              });

              form.validateFields({ validateOnly: true }).then(
                (_) => {
                  dispatch(
                    variableDetailUpdate(updatedVariable as VariableInfo, true),
                  );
                },
                () => {
                  dispatch(
                    variableDetailUpdate(
                      updatedVariable as VariableInfo,
                      false,
                    ),
                  );
                },
              );

              // When enabled, set minimum mandatory facets
              // The useEffect will handle adding missing facets to main facets field

              // Scroll to the end of the modal when enabling mandatory facets
              const modal = document.querySelector(
                '.variable-settings-modal .dui-modal-body .dui-tabs-content-holder',
              );
              if (modal) {
                setTimeout(() => {
                  modal.scrollTo({
                    top: modal.scrollHeight,
                    behavior: 'smooth',
                  });
                }, 100);
              }
            }}
          />
        </div>

        {isMandatoryFacetEnabled && (
          <Form.Item
            dependencies={['facets']}
            name={['metadata', 'mandatoryFacets']}
          >
            <CommonSelect
              id="variableDetails-mandatory-facets"
              className={
                isFieldChanged(variableDiff, 'metadata/mandatoryFacets')
                  ? 'changelog-item-variable-modified'
                  : ''
              }
              data-testid="variable-details-mandatory-facets"
              disabled={disabled}
              values={availableFacets}
              disabledMap={mandatoryFacetsDisabledMap}
              idKey="name"
              valueKey="title"
              multiple={true}
              placeholder="Select mandatory facets for rollouts"
              tagRender={customTagRender}
            />
          </Form.Item>
        )}
      </div>
    </Card>
  );
};

const VariableDetail = ({
  style,
  onManageEntitiesClick = () => {},
  onManageEntitiesCancel = () => {},
}: {
  style?: React.CSSProperties;
  onManageEntitiesClick?: () => void;
  onManageEntitiesCancel?: () => void;
}) => {
  const { variable, disabled: variableDisabled } = useVariableContext();
  const dispatch = useVariableDispatchContext();

  // Check if variable is synced from Grab (tenant portal)
  const isSyncedFromGrab = useIsSyncedFromGrab(variable);

  const [form] = Form.useForm();
  form.setFieldsValue(variable);

  useEffect(() => {
    // Skip validation if disabled
    if (variableDisabled) return;

    form.validateFields({ validateOnly: true }).then(
      () => {
        dispatch(updateVariableIsValid(true));
      },
      () => {
        dispatch(updateVariableIsValid(false));
      },
    );
  }, [variable?.name, variableDisabled]);
  // Create a debounced version of the form validation and logging
  const debouncedHandleFormValuesChange = useCallback(
    _.debounce(
      async (variable: VariableInfo, changedValues: any, allFields: any) => {
        const nextAllFields = produce(allFields, (draft) => {
          Object.keys(changedValues).forEach((key) => {
            switch (key) {
              case 'domain': {
                draft.assignBy = getDomainAssignBy(changedValues.domain);

                const facets: string[] = _.union<string>(
                  draft.facets,
                  getMandatoryFacetsByDomain(changedValues.domain),
                );

                if (facets.length !== draft.facets?.length) {
                  draft.facets = facets;
                }

                break;
              }
              case 'facets': {
                draft.facets = _.union(
                  changedValues.facets,
                  getMandatoryFacetsByDomain[allFields.domain],
                );

                break;
              }
              case 'public': {
                if (!changedValues.public) {
                  draft.hot = false;
                }

                break;
              }
              case 'metadata':
                Object.keys(changedValues.metadata).forEach((metadataKey) => {
                  switch (metadataKey) {
                    case 'mandatoryFacets':
                      {
                        draft.facets = union(draft.facets, [
                          ...MinimumMandatoryFacets,
                        ]);
                      }
                      break;
                  }
                });
                break;
              default:
                break;
            }
          });
        });

        const updatedVariable = deepMerge(variable, nextAllFields);

        form.validateFields({ validateOnly: true }).then(
          (_) => {
            dispatch(
              variableDetailUpdate(updatedVariable as VariableInfo, true),
            );
          },
          () => {
            dispatch(
              variableDetailUpdate(updatedVariable as VariableInfo, false),
            );
          },
        );
      },
      500,
    ),
    [variable],
  );

  const handleFormValuesChange = (changedValues: any, allFields: any) => {
    debouncedHandleFormValuesChange(variable, changedValues, allFields);
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Form: {
            itemMarginBottom: 0,
          },
        },
      }}
    >
      <Form
        layout="vertical"
        form={form}
        preserve
        onValuesChange={handleFormValuesChange}
        scrollToFirstError
        disabled={isSyncedFromGrab}
        style={{
          ...style,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            position: 'relative',
          }}
        >
          <SyncedFromGrabOverlay show={isSyncedFromGrab} />
          <MainDetails />
          <Owners
            onManageEntitiesClick={onManageEntitiesClick}
            onManageEntitiesCancel={onManageEntitiesCancel}
          />
          <AnalyticsExperimentation />
          <TechnicalDetails />
        </div>
      </Form>
    </ConfigProvider>
  );
};

export default VariableDetail;
